import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  TextField,
  Switch,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  CircularProgress,
  Grid,
  Radio,
  RadioGroup,
  FormControlLabel,
  Checkbox,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  IconButton,
  OutlinedInput,
  Avatar,
  Autocomplete,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import ClearIcon from "@mui/icons-material/Clear";
import { FIELD_TYPES } from "app/utils/constants/constants";
import Div from "@jumbo/shared/Div";
import { useSnackbar } from "notistack";
import BulkUpdateDialog from "components/BulkUpdateDialog";
import LoadingView from "components/LoadingView";
import ErrorView from "components/ErrorView";
import { formatDate } from "app/utils/dateUtils";
import entityService from "app/services/entity-service";
import { debounce } from "lodash";

// Helper functions
const getOptionsArray = (options) => {
  if (Array.isArray(options)) {
    return options;
  }
  if (options && typeof options === "object") {
    return Object.keys(options);
  }
  return [];
};

const initializeNestedObject = (nestedFields) => {
  const obj = {};
  Object.entries(nestedFields).forEach(([key, field]) => {
    if (field.type === FIELD_TYPES.NESTED_ARRAY) {
      obj[key] = [];
    } else if (
      field.type === FIELD_TYPES.BOOLEAN ||
      field.type === FIELD_TYPES.CHECKBOX
    ) {
      obj[key] = false;
    } else if (field.type === FIELD_TYPES.NUMBER) {
      obj[key] = 0;
    } else if (
      field.type === FIELD_TYPES.FILE ||
      field.type === FIELD_TYPES.IMAGE ||
      field.type === FIELD_TYPES.FOREIGN_KEY
    ) {
      obj[key] = null;
    } else {
      obj[key] = "";
    }
  });
  return obj;
};

const isEmptyObject = (obj, nestedFields) => {
  return Object.entries(nestedFields).every(([key, field]) => {
    const value = obj[key];
    if (field.type === FIELD_TYPES.NESTED_ARRAY) {
      return Array.isArray(value) && value.length === 0;
    } else if (
      field.type === FIELD_TYPES.BOOLEAN ||
      field.type === FIELD_TYPES.CHECKBOX
    ) {
      return value === false;
    } else if (field.type === FIELD_TYPES.NUMBER) {
      return value === 0;
    } else if (
      field.type === FIELD_TYPES.FILE ||
      field.type === FIELD_TYPES.IMAGE ||
      field.type === FIELD_TYPES.FOREIGN_KEY
    ) {
      return value === null;
    } else {
      return value === "";
    }
  });
};

const getSummaryField = (nestedFields) => {
  for (const [key, field] of Object.entries(nestedFields)) {
    if (
      field.type === FIELD_TYPES.STRING ||
      field.type === FIELD_TYPES.TEXTAREA
    ) {
      return key;
    }
  }
  return null;
};

const validateField = (value, field, path = [], mode) => {
  const errors = [];
  const prefix = path.length > 0 ? `${path.join(" - ")} - ` : "";
  const label = field.label || "Field";

  // Required validation, skip for id in add mode
  if (field.required && !field.validation?.optional && !(field.label === "ID" && mode === "add")) {
    if (
      value === undefined ||
      value === null ||
      (typeof value === "string" && value.trim() === "") ||
      (Array.isArray(value) && value.length === 0)
    ) {
      errors.push(`${prefix}${label} is required`);
    }
  }

  if (value !== undefined && value !== null && value !== "") {
    if (field.validation?.regex) {
      if (!field.validation.regex.test(value)) {
        errors.push(`${prefix}${field.validation.message}`);
      }
    }
    if (field.type === FIELD_TYPES.NUMBER) {
      if (
        field.validation?.min !== undefined &&
        Number(value) < field.validation.min
      ) {
        errors.push(`${prefix}${field.validation.message}`);
      }
      if (
        field.validation?.max !== undefined &&
        Number(value) > field.validation.max
      ) {
        errors.push(`${prefix}${field.validation.message}`);
      }
    }
    if (field.type === FIELD_TYPES.ENUM) {
      const validOptions = getOptionsArray(field.options);
      if (!validOptions.includes(value)) {
        errors.push(
          `${prefix}${label} must be one of: ${validOptions
            .map((opt) => (field.labelMap ? field.labelMap[opt] : opt))
            .join(", ")}`
        );
      }
    }
    if (field.type === FIELD_TYPES.ARRAY) {
      const validOptions = getOptionsArray(field.options);
      (value || []).forEach((item, idx) => {
        if (!validOptions.includes(item)) {
          errors.push(
            `${prefix}${label} item ${idx + 1} must be one of: ${validOptions
              .map((opt) => (field.labelMap ? field.labelMap[opt] : opt))
              .join(", ")}`
          );
        }
      });
    }
    if (field.type === FIELD_TYPES.FOREIGN_KEY) {
      if (
        field.foreignKeyConfig?.valueField &&
        (value === null || value === undefined)
      ) {
        errors.push(`${prefix}${label} must have a valid selection`);
      }
    }
    if (field.type === FIELD_TYPES.NESTED_ARRAY && Array.isArray(value)) {
      value.forEach((item, index) => {
        Object.entries(field.nestedFields).forEach(
          ([nestedKey, nestedField]) => {
            const nestedErrors = validateField(item[nestedKey], nestedField, [
              ...path,
              `${item.label || label} ${index + 1}`,
            ], mode);
            errors.push(...nestedErrors);
          }
        );
      });
    }
  }

  return errors;
};

const GenericEntityForm = ({
  entityConfig,
  id,
  mode = "view",
  fetchService,
  createService,
  updateService,
  title = mode === "view"
    ? "Details"
    : mode === "add"
    ? "Add Entity"
    : "Update Entity",
  addButtonLabel = "Add",
  updateButtonLabel = "Update",
  cancelButtonLabel = "Cancel",
  backButtonLabel = "Back",
  errorMessage = "Failed to save data",
  loadingMessage = "Loading...",
  invalidConfigMessage = "Invalid entity configuration",
  onSuccessNavigation = (newId) =>
    `${entityConfig.uiPaths?.list?.path}/${newId || id}`,
  noDataMessage = "No data available",
}) => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const [formData, setFormData] = useState({});
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [bulkUpdateField, setBulkUpdateField] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [expandedAccordions, setExpandedAccordions] = useState({});
  const [foreignKeyOptions, setForeignKeyOptions] = useState({});
  const fetchedFieldsRef = useRef(new Set());
  const isMountedRef = useRef(true);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const fetchForeignKeyOptions = useCallback(
    async (key, field, searchQuery = "", isSpecificFetch = false) => {
      if (!field.foreignKeyConfig?.apiPath || !isMountedRef.current) return;

      const fetchKey = `${key}:${searchQuery || "initial"}:${
        isSpecificFetch ? "specific" : "general"
      }`;
      if (fetchedFieldsRef.current.has(fetchKey)) {
        return;
      }
      fetchedFieldsRef.current.add(fetchKey);

      try {
        const params = field.foreignKeyConfig.params || {
          page: 0,
          size: 20,
          sort: `${field.foreignKeyConfig.labelField},asc`,
        };

        const page = params.page || 0;
        const size = params.size || 20;
        const [sortBy, sortOrder] = params.sort
          ? params.sort.split(",")
          : [field.foreignKeyConfig.labelField || "id", "asc"];

        const additionalParams = { ...params };
        if (searchQuery && field.foreignKeyConfig.searchField) {
          const [fieldName, operation] =
            field.foreignKeyConfig.searchField.split(".");
          if (operation) {
            additionalParams[`${fieldName}.${operation}`] = searchQuery;
          } else {
            additionalParams[field.foreignKeyConfig.searchField] = searchQuery;
          }
        }
        delete additionalParams.page;
        delete additionalParams.size;
        delete additionalParams.sort;

        const response = await entityService.fetchList(
          field.foreignKeyConfig.apiPath,
          page,
          size,
          sortBy,
          sortOrder,
          additionalParams
        );

        const options = Array.isArray(response.data)
          ? response.data
          : Array.isArray(response.content)
          ? response.content
          : Array.isArray(response)
          ? response
          : [];

        if (isMountedRef.current) {
          setForeignKeyOptions((prev) => ({
            ...prev,
            [key]: options,
          }));
        }
      } catch (err) {
        if (isMountedRef.current) {
          const errorMsg =
            err.response?.data?.message || err.message || "Unknown error";
          enqueueSnackbar(
            `Failed to fetch options for ${field.label}: ${errorMsg}`,
            { variant: "error" }
          );
        }
      }
    },
    [enqueueSnackbar]
  );

  const debouncedFetchForeignKeyOptions = useCallback(
    debounce((key, field, searchQuery) => {
      fetchForeignKeyOptions(key, field, searchQuery, false);
    }, 300),
    [fetchForeignKeyOptions]
  );

  useEffect(() => {
    if (
      !entityConfig ||
      !entityConfig.entityStructure ||
      !entityConfig.entityStructure.fields ||
      !entityConfig.backendApiPaths
    ) {
      enqueueSnackbar(invalidConfigMessage, { variant: "error" });
      setError(invalidConfigMessage);
      setIsLoading(false);
      return;
    }

    const initialFetch = async () => {
      setIsLoading(true);
      fetchedFieldsRef.current.clear();

      const foreignKeyFields = Object.entries(
        entityConfig.entityStructure.fields
      ).filter(([_, field]) => field.type === FIELD_TYPES.FOREIGN_KEY);

      try {
        // Step 1: Fetch initial options
        await Promise.all(
          foreignKeyFields.map(([key, field]) =>
            fetchForeignKeyOptions(key, field, "", false)
          )
        );

        if (id && (mode === "view" || mode === "update")) {
          const res = await fetchService(
            entityConfig.backendApiPaths.getById,
            id
          );
          const updatedFormData = { ...res };
          Object.keys(entityConfig.entityStructure.fields).forEach((key) => {
            if (
              !(key in updatedFormData) &&
              !entityConfig.entityStructure.fields[key].isHiddenInForm
            ) {
              updatedFormData[key] = null;
            }
          });

          // Step 2: Map FOREIGN_KEY values and collect specific fetches
          const specificFetches = [];
          const resolvedFormData = { ...updatedFormData };

          for (const [key, field] of foreignKeyFields) {
            if (
              field.type === FIELD_TYPES.FOREIGN_KEY &&
              updatedFormData[key] != null
            ) {
              const options = foreignKeyOptions[key] || [];
              const selectedOption = options.find(
                (opt) =>
                  String(opt[field.foreignKeyConfig.valueField]) ===
                  String(updatedFormData[key])
              );
              if (selectedOption) {
                resolvedFormData[key] =
                  selectedOption[field.foreignKeyConfig.valueField];
              } else if (field.foreignKeyConfig.apiPath) {
                specificFetches.push({
                  key,
                  field,
                  value: String(updatedFormData[key]),
                });
              }
            }
          }

          // Step 3: Fetch specific options if needed
          if (specificFetches.length > 0) {
            await Promise.all(
              specificFetches.map(({ key, field, value }) =>
                fetchForeignKeyOptions(key, field, value, true)
              )
            );

            // Step 4: Final mapping after specific fetches
            for (const { key, field } of specificFetches) {
              const options = foreignKeyOptions[key] || [];
              const selectedOption = options.find(
                (opt) =>
                  String(opt[field.foreignKeyConfig.valueField]) ===
                  String(resolvedFormData[key])
              );
              if (selectedOption) {
                resolvedFormData[key] =
                  selectedOption[field.foreignKeyConfig.valueField];
              }
            }
          }

          if (isMountedRef.current) {
            setFormData(resolvedFormData);
            setError(null);
          }
        } else if (mode === "add") {
          const initialData = Object.keys(
            entityConfig.entityStructure.fields
          ).reduce((acc, key) => {
            const field = entityConfig.entityStructure.fields[key];
            if (!field.isHiddenInForm) {
              if (key === "id") {
                acc[key] = null;
              } else {
                acc[key] =
                  field.type === FIELD_TYPES.NESTED_ARRAY ||
                  field.type === FIELD_TYPES.ARRAY
                    ? []
                    : field.type === FIELD_TYPES.BOOLEAN ||
                      field.type === FIELD_TYPES.CHECKBOX
                    ? false
                    : field.type === FIELD_TYPES.NUMBER
                    ? 0
                    : field.type === FIELD_TYPES.FILE ||
                      field.type === FIELD_TYPES.IMAGE ||
                      field.type === FIELD_TYPES.FOREIGN_KEY
                    ? null
                    : "";
              }
            }
            return acc;
          }, {});
          if (isMountedRef.current) {
            setFormData(initialData);
          }
        }
      } catch (err) {
        if (isMountedRef.current) {
          setError(err.message || errorMessage);
          setFormData(null);
        }
      } finally {
        if (isMountedRef.current) {
          setIsLoading(false);
        }
      }
    };

    initialFetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    entityConfig,
    id,
    mode,
    enqueueSnackbar,
    fetchService,
    invalidConfigMessage,
    errorMessage,
    fetchForeignKeyOptions,
  ]);

  const handleChange = (key, value, path = []) => {
    setFormData((prev) => {
      if (path.length === 0) {
        return { ...prev, [key]: value };
      }
      const newFormData = JSON.parse(JSON.stringify(prev));
      let current = newFormData;
      for (let i = 0; i < path.length - 1; i++) {
        current = current[path[i]];
      }
      current[path[path.length - 1]][key] = value;
      return newFormData;
    });
  };

  const handleFileChange = (key, event, path = [], field) => {
    if (event === null) {
      handleChange(key, null, path);
      return;
    }
    const file = event.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        enqueueSnackbar("File size should not exceed 5MB", {
          variant: "error",
        });
        return;
      }
      if (field.type === FIELD_TYPES.IMAGE && !file.type.startsWith("image/")) {
        enqueueSnackbar("Please upload a valid image file", {
          variant: "error",
        });
        return;
      }
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64String = reader.result.split(",")[1];
        handleChange(key, base64String, path);
      };
      reader.onerror = () => {
        enqueueSnackbar("Failed to read the file", { variant: "error" });
      };
      reader.readAsDataURL(file);
    }
  };

  const handleAddNestedObject = (key, nestedFields, path = []) => {
    setFormData((prev) => {
      const newFormData = JSON.parse(JSON.stringify(prev));
      let current = newFormData;
      for (const p of path) {
        current = current[p];
      }
      current[key] = [
        ...(current[key] || []),
        initializeNestedObject(nestedFields),
      ];
      return newFormData;
    });
  };

  const handleRemoveNestedObject = (key, index, path = []) => {
    setFormData((prev) => {
      const newFormData = JSON.parse(JSON.stringify(prev));
      let current = newFormData;
      for (const p of path) {
        current = current[p];
      }
      current[key] = current[key].filter((_, i) => i !== index);
      return newFormData;
    });
  };

  const handleBulkUpdate = async (key, path, data, index) => {
    setFormData((prev) => {
      const newFormData = JSON.parse(JSON.stringify(prev));
      let current = newFormData;
      for (const p of path) {
        current = current[p];
      }
      const currentArray = current[key] || [];

      if (
        index !== undefined &&
        currentArray[index] &&
        isEmptyObject(currentArray[index], bulkUpdateField.nestedFields)
      ) {
        currentArray[index] = data[0];
        if (data.length > 1) {
          currentArray.push(...data.slice(1));
        }
      } else {
        currentArray.push(...data);
      }

      current[key] = currentArray;
      return newFormData;
    });
  };

  const handleAccordionChange = (accordionKey) => (event, isExpanded) => {
    setExpandedAccordions((prev) => ({
      ...prev,
      [accordionKey]: isExpanded,
    }));
  };

  const renderField = (key, field, value, path = [], isEditMode) => {
    if (field.isHiddenInForm && isEditMode) return null;

    const disabled = !isEditMode;
    const fieldPath = path.length === 0 ? key : [...path, key].join(".");
    const errorMessage = errors[fieldPath];

    if (key === "id" && mode === "add") return null; // Hide id field in add mode

    if (field.type === FIELD_TYPES.NESTED_ARRAY) {
      const items = value || [];
      const summaryField = getSummaryField(field.nestedFields);

      return (
        <Box sx={{ mb: 2 }}>
          {items.length > 0 && (
            <Typography variant="h6">{field.label}</Typography>
          )}
          {items.map((item, index) => {
            const accordionKey = `${key}-${index}-${path.join("-")}`;
            const isExpanded = !!expandedAccordions[accordionKey];
            const summaryValue =
              summaryField && item[summaryField]
                ? item[summaryField]
                : `${field.label} ${index + 1}`;

            return (
              <Accordion
                key={index}
                sx={{
                  mb: 1,
                  borderRadius: 2,
                  "&::before": { height: 0 },
                  "& .MuiAccordionSummary-content": { my: "20px" },
                }}
                expanded={isExpanded}
                onChange={handleAccordionChange(accordionKey)}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  sx={{
                    bgcolor: "background.default",
                    borderRadius: 2,
                    "&:hover": { bgcolor: "action.hover" },
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      width: "100%",
                    }}
                  >
                    <Typography sx={{ flex: 1 }}>{summaryValue}</Typography>
                    {isEditMode && isExpanded && (
                      <>
                        <Button
                          variant="outlined"
                          size="small"
                          startIcon={<AddIcon />}
                          onClick={(e) => {
                            e.stopPropagation();
                            setBulkUpdateField({
                              key,
                              path,
                              nestedFields: field.nestedFields,
                              index,
                            });
                            setOpenDialog(true);
                          }}
                          sx={{ mr: 1 }}
                        >
                          Bulk Update
                        </Button>
                        <IconButton
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRemoveNestedObject(key, index, path);
                          }}
                        >
                          <DeleteIcon color="error" />
                        </IconButton>
                      </>
                    )}
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  <Grid container spacing={2}>
                    {Object.entries(field.nestedFields).map(
                      ([nestedKey, nestedField]) => (
                        <Grid
                          item
                          xs={nestedField.editGridXs || 12}
                          sm={nestedField.editGridSm || 6}
                          key={nestedKey}
                        >
                          {renderField(
                            nestedKey,
                            nestedField,
                            item[nestedKey],
                            [...path, key, index],
                            isEditMode
                          )}
                        </Grid>
                      )
                    )}
                  </Grid>
                </AccordionDetails>
              </Accordion>
            );
          })}
          {isEditMode && (
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={() =>
                handleAddNestedObject(key, field.nestedFields, path)
              }
              sx={{ mt: 1 }}
            >
              Add {field.label}
            </Button>
          )}
        </Box>
      );
    }

    if (field.type === FIELD_TYPES.ARRAY) {
      return (
        <FormControl fullWidth disabled={disabled} error={!!errorMessage}>
          <InputLabel>{field.label}</InputLabel>
          <Select
            multiple
            required={field.required}
            value={value || []}
            onChange={(e) => handleChange(key, e.target.value, path)}
            input={<OutlinedInput label={field.label} />}
            renderValue={(selected) =>
              selected
                .map((item) =>
                  field.labelMap ? field.labelMap[item] || item : item
                )
                .join(", ")
            }
            sx={
              disabled
                ? {
                    "& .MuiInputBase-input.MuiOutlinedInput-input.Mui-disabled":
                      { textFillColor: "#475259" },
                  }
                : {}
            }
          >
            {getOptionsArray(field.options).map((option) => (
              <MenuItem key={option} value={option}>
                {field.labelMap ? field.labelMap[option] || option : option}
              </MenuItem>
            ))}
          </Select>
          {errorMessage && (
            <Typography color="error" variant="caption">
              {errorMessage}
            </Typography>
          )}
        </FormControl>
      );
    }

    if (
      field.type === FIELD_TYPES.BOOLEAN ||
      field.type === FIELD_TYPES.CHECKBOX
    ) {
      return (
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <Typography sx={{ mr: 2, flex: 1 }}>{field.label}</Typography>
          {field.type === FIELD_TYPES.BOOLEAN ? (
            <Switch
              checked={value || false}
              onChange={(e) => handleChange(key, e.target.checked, path)}
              disabled={disabled}
              sx={
                disabled
                  ? {
                      "& .MuiInputBase-input.MuiOutlinedInput-input.Mui-disabled":
                        { textFillColor: "#475259" },
                    }
                  : {}
              }
            />
          ) : (
            <Checkbox
              checked={value || false}
              onChange={(e) => handleChange(key, e.target.checked, path)}
              disabled={disabled}
            />
          )}
          {errorMessage && (
            <Typography color="error" variant="caption">
              {errorMessage}
            </Typography>
          )}
        </Box>
      );
    }

    if (field.type === FIELD_TYPES.DATE) {
      return (
        <TextField
          fullWidth
          label={field.label}
          value={isEditMode ? value || "" : formatDate(value) || "-"}
          onChange={(e) => handleChange(key, e.target.value, path)}
          type={isEditMode ? "datetime-local" : "text"}
          InputLabelProps={{ shrink: true }}
          required={field.required}
          disabled={disabled}
          InputProps={{ readOnly: disabled }}
          error={!!errorMessage}
          helperText={errorMessage}
          sx={
            disabled
              ? {
                  "& .MuiInputBase-input.MuiOutlinedInput-input.Mui-disabled": {
                    textFillColor: "#475259",
                  },
                }
              : {}
          }
        />
      );
    }

    if (field.type === FIELD_TYPES.ENUM || field.type === FIELD_TYPES.SELECT) {
      return (
        <FormControl fullWidth disabled={disabled} error={!!errorMessage}>
          <InputLabel>{field.label}</InputLabel>
          <Select
            value={value || ""}
            onChange={(e) => handleChange(key, e.target.value, path)}
            required={field.required}
            input={<OutlinedInput label={field.label} />}
            sx={
              disabled
                ? {
                    "& .MuiInputBase-input.MuiOutlinedInput-input.Mui-disabled":
                      { textFillColor: "#475259" },
                  }
                : {}
            }
          >
            {getOptionsArray(field.options).map((option) => (
              <MenuItem key={option} value={option}>
                {field.labelMap ? field.labelMap[option] || option : option}
              </MenuItem>
            ))}
          </Select>
          {errorMessage && (
            <Typography color="error" variant="caption">
              {errorMessage}
            </Typography>
          )}
        </FormControl>
      );
    }

    if (field.type === FIELD_TYPES.FOREIGN_KEY) {
      const options = Array.isArray(foreignKeyOptions[key])
        ? foreignKeyOptions[key]
        : [];
      const selectedOption =
        options.find(
          (opt) =>
            String(opt?.[field.foreignKeyConfig.valueField]) === String(value)
        ) || null;

      if (!isEditMode) {
        const displayValue = selectedOption
          ? selectedOption[field.foreignKeyConfig.labelField]
          : value || "No data available";
        return (
          <TextField
            fullWidth
            label={field.label}
            value={displayValue}
            disabled={true}
            InputProps={{ readOnly: true }}
            sx={{
              "& .MuiInputBase-input.Mui-disabled": {
                textFillColor: "#475259",
              },
            }}
          />
        );
      }

      return (
        <Autocomplete
          fullWidth
          disabled={disabled}
          options={options}
          getOptionLabel={(option) =>
            option?.[field.foreignKeyConfig.labelField] || ""
          }
          value={selectedOption}
          onChange={(event, newValue) => {
            handleChange(
              key,
              newValue ? newValue[field.foreignKeyConfig.valueField] : null,
              path
            );
          }}
          onInputChange={(event, newInputValue, reason) => {
            if (reason === "input") {
              debouncedFetchForeignKeyOptions(key, field, newInputValue);
            }
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label={field.label}
              required={field.required}
              error={!!errorMessage}
              helperText={
                errorMessage ||
                (options.length === 0 ? "No options available" : undefined)
              }
              InputProps={{
                ...params.InputProps,
                readOnly: disabled,
              }}
              sx={
                disabled
                  ? {
                      "& .MuiInputBase-input.MuiOutlinedInput-input.Mui-disabled":
                        { textFillColor: "#475259" },
                    }
                  : {}
              }
            />
          )}
          filterOptions={(options, { inputValue }) => options}
          isOptionEqualToValue={(option, value) =>
            option && value
              ? String(option[field.foreignKeyConfig.valueField]) ===
                String(value)
              : option === value
          }
        />
      );
    }

    if (field.type === FIELD_TYPES.RADIO) {
      return (
        <FormControl
          component="fieldset"
          fullWidth
          disabled={disabled}
          error={!!errorMessage}
        >
          <Typography>{field.label}</Typography>
          <RadioGroup
            value={value || ""}
            onChange={(e) => handleChange(key, e.target.value, path)}
          >
            {getOptionsArray(field.options).map((option) => (
              <FormControlLabel
                key={option}
                value={option}
                control={<Radio />}
                label={
                  field.labelMap ? field.labelMap[option] || option : option
                }
                disabled={disabled}
              />
            ))}
          </RadioGroup>
          {errorMessage && (
            <Typography color="error" variant="caption">
              {errorMessage}
            </Typography>
          )}
        </FormControl>
      );
    }

    if (field.type === FIELD_TYPES.IMAGE || field.type === FIELD_TYPES.FILE) {
      const imageSrc =
        value && formData.imageContentType
          ? `data:${formData.imageContentType};base64,${value}`
          : "";

      const handleImageClick = () => {
        if (imageSrc) {
          const newTab = window.open("", "_blank");
          if (newTab) {
            newTab.document.write(`
              <html>
                <head>
                  <title>${field.label}</title>
                </head>
                <body style="margin: 0; display: flex; justify-content: center; align-items: center; height: 100vh; background: #000;">
                  <img src="${imageSrc}" style="max-width: 100%; max-height: 100%; object-fit: contain;" alt="${field.label}" />
                </body>
              </html>
            `);
            newTab.document.close();
          } else {
            enqueueSnackbar("Failed to open image in new tab", {
              variant: "error",
            });
          }
        }
      };

      return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          {isEditMode ? (
            <TextField
              fullWidth
              label={field.label}
              type="file"
              onChange={(e) => handleFileChange(key, e, path, field)}
              InputLabelProps={{ shrink: true }}
              inputProps={{
                accept:
                  field.accept ||
                  (field.type === FIELD_TYPES.IMAGE ? "image/*" : "*"),
              }}
              required={field.required && !value}
              disabled={disabled}
              InputProps={{
                readOnly: disabled,
              }}
              error={!!errorMessage}
              helperText={
                errorMessage ||
                (isEditMode && value
                  ? "Select a new file to replace the existing one"
                  : undefined)
              }
              sx={
                disabled
                  ? {
                      "& .css-1tyy9uy-MuiFormControl-root-MuiTextField-root .MuiOutlinedInput-input.Mui-disabled":
                        {
                          color: "#000000",
                        },
                    }
                  : {}
              }
            />
          ) : (
            <Typography variant="body1">{field.label}</Typography>
          )}
          {value && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              {field.type === FIELD_TYPES.IMAGE ? (
                <Avatar
                  src={imageSrc}
                  alt={field.label}
                  sx={{
                    width: 300,
                    height: 300,
                    mb: 1,
                    "& .MuiAvatar-img": { objectFit: "contain" },
                    cursor: value ? "pointer" : "default",
                  }}
                  variant="rounded"
                  onClick={handleImageClick}
                />
              ) : (
                <Typography variant="body2">File uploaded</Typography>
              )}
              {isEditMode && (
                <IconButton
                  onClick={() => handleFileChange(key, null, path, field)}
                  size="small"
                  color="error"
                >
                  <ClearIcon />
                </IconButton>
              )}
            </Box>
          )}
          {!value && !isEditMode && (
            <Typography variant="body2" color="text.secondary">
              No file available
            </Typography>
          )}
        </Box>
      );
    }

    if (field.type === FIELD_TYPES.OBJECT) {
      return (
        <TextField
          fullWidth
          label={field.label}
          value={value ? JSON.stringify(value) : ""}
          onChange={(e) => {
            try {
              const newValue = e.target.value
                ? JSON.parse(e.target.value)
                : null;
              handleChange(key, newValue, path);
            } catch (err) {
              enqueueSnackbar("Invalid JSON format", { variant: "error" });
            }
          }}
          multiline
          rows={3}
          required={field.required}
          disabled={disabled}
          InputProps={{ readOnly: disabled }}
          error={!!errorMessage}
          helperText={errorMessage}
          sx={
            disabled
              ? { "& .MuiInputLabel-root.Mui-disabled": { color: "#8595A6" } }
              : {}
          }
        />
      );
    }

    if (field.type === FIELD_TYPES.NUMBER) {
      return (
        <TextField
          fullWidth
          label={field.label}
          value={value === 0 ? "0" : value || ""}
          onChange={(e) => {
            const inputValue = e.target.value;
            handleChange(
              key,
              inputValue === "" ? "" : Number(inputValue),
              path
            );
          }}
          type="number"
          required={field.required}
          disabled={disabled}
          InputProps={{ readOnly: disabled }}
          placeholder={field.placeholder}
          error={!!errorMessage}
          helperText={errorMessage}
          sx={
            disabled
              ? {
                  "& .MuiInputBase-input.MuiOutlinedInput-input.Mui-disabled": {
                    textFillColor: "#475259",
                  },
                }
              : {}
          }
          inputProps={{
            min: field.validation?.min || 0,
            step: 1,
          }}
        />
      );
    }

    if (field.type === FIELD_TYPES.PASSWORD) {
      return (
        <TextField
          fullWidth
          label={field.label}
          value={value || ""}
          onChange={(e) => handleChange(key, e.target.value, path)}
          type="password"
          required={field.required}
          disabled={disabled}
          InputProps={{ readOnly: disabled }}
          error={!!errorMessage}
          helperText={errorMessage}
          sx={
            disabled
              ? { "& .MuiInputLabel-root.Mui-disabled": { color: "#8595A6" } }
              : {}
          }
        />
      );
    }

    if (
      field.type === FIELD_TYPES.TEXTAREA ||
      field.type === FIELD_TYPES.HTML
    ) {
      return (
        <TextField
          fullWidth
          label={field.label}
          value={value || ""}
          onChange={(e) => handleChange(key, e.target.value, path)}
          multiline
          rows={field.type === FIELD_TYPES.HTML ? 6 : 4}
          required={field.required}
          disabled={disabled}
          InputProps={{ readOnly: disabled }}
          error={!!errorMessage}
          helperText={errorMessage}
          sx={
            disabled
              ? {
                  "& .MuiInputBase-input.MuiOutlinedInput-input.Mui-disabled": {
                    textFillColor: "#475259",
                  },
                }
              : {}
          }
        />
      );
    }

    return (
      <TextField
        fullWidth
        label={field.label}
        value={value || ""}
        onChange={(e) => handleChange(key, e.target.value, path)}
        type="text"
        required={field.required}
        disabled={disabled}
        InputProps={{ readOnly: disabled }}
        error={!!errorMessage}
        helperText={errorMessage}
        sx={
          disabled
            ? {
                "& .MuiInputBase-input.MuiOutlinedInput-input.Mui-disabled": {
                  textFillColor: "#475259",
                },
              }
            : {}
        }
      />
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    const allErrors = {};
    const validateNested = (fields, data, parentPath = [], parentData = {}) => {
      Object.entries(fields).forEach(([key, field]) => {
        if (!field.isHiddenInForm) {
          const fieldPath = [...parentPath, key].join(".");
          const value = parentPath.length === 0 ? data[key] : parentData[key];
          const fieldErrors = validateField(value, field, parentPath, mode);
          if (fieldErrors.length > 0) {
            allErrors[fieldPath] = fieldErrors[0];
          }
          if (field.type === FIELD_TYPES.NESTED_ARRAY && Array.isArray(value)) {
            value.forEach((item, index) => {
              validateNested(
                field.nestedFields,
                data,
                [...parentPath, key, index],
                item
              );
            });
          }
        }
      });
    };

    validateNested(entityConfig.entityStructure.fields, formData, [], formData);

    if (Object.keys(allErrors).length > 0) {
      setErrors(allErrors);
      enqueueSnackbar("Please correct the errors in the form", {
        variant: "error",
      });
      setIsLoading(false);
      return;
    }

    try {
      const payload = Object.keys(formData).reduce((acc, key) => {
        const field = entityConfig.entityStructure.fields[key];
        if (field?.isIncludedInPayload && !(mode === "add" && key === "id")) {
          acc[key] = formData[key];
        }
        return acc;
      }, {});
      if (mode === "update") {
        await updateService(
          entityConfig.backendApiPaths.update.replace(":id", id),
          payload
        );
        enqueueSnackbar(`${formData.id || "Entity"} updated successfully`, {
          variant: "success",
        });
        navigate(onSuccessNavigation(id));
      } else if (mode === "add") {
        const response = await createService(
          entityConfig.backendApiPaths.create,
          payload
        );
        const newId = response.id;
        enqueueSnackbar(`${newId || "Entity"} created successfully`, {
          variant: "success",
        });
        navigate(onSuccessNavigation(newId));
      }
    } catch (err) {
      enqueueSnackbar(err.message || errorMessage, { variant: "error" });
    } finally {
      setIsLoading(false);
    }
  };

  const handleBack = () => {
    navigate(entityConfig.uiPaths?.list?.path || -1);
  };

  if (isLoading) {
    return <LoadingView message={loadingMessage} />;
  }

  if (error && mode !== "add") {
    return (
      <ErrorView
        error={error}
        onBack={handleBack}
        backButtonLabel={backButtonLabel}
      />
    );
  }

  if (!formData && mode !== "add") {
    return (
      <ErrorView
        error={noDataMessage}
        onBack={handleBack}
        backButtonLabel={backButtonLabel}
      />
    );
  }

  const isEditMode = mode === "add" || mode === "update";

  return (
    <Div sx={{ p: 2 }}>
      <Typography variant="h4" gutterBottom sx={{ mb: 4, marginLeft: 3 }}>
        {title}
      </Typography>
      {isEditMode ? (
        <form onSubmit={handleSubmit}>
          <Grid container spacing={3} sx={{ px: 3 }}>
            {Object.entries(entityConfig.entityStructure.fields).map(
              ([key, field]) => {
                if (key === "id" && mode === "add") return null;
                if (field.isHiddenInForm) return null;
                return (
                  <Grid
                    item
                    xs={field.editGridXs || 12}
                    sm={field.editGridSm || 6}
                    key={key}
                  >
                    {renderField(key, field, formData[key], [], isEditMode)}
                  </Grid>
                );
              }
            )}
          </Grid>
          <Box
            sx={{ mt: 4, display: "flex", gap: 2, justifyContent: "flex-end" }}
          >
            <Button
              variant="contained"
              onClick={handleBack}
              disabled={isLoading}
              color="error"
            >
              {cancelButtonLabel}
            </Button>
            <Button
              variant="contained"
              disabled={isLoading}
              startIcon={
                isLoading ? (
                  <CircularProgress size={20} color="inherit" />
                ) : null
              }
              type="submit"
            >
              {mode === "add" ? addButtonLabel : updateButtonLabel}
            </Button>
          </Box>
        </form>
      ) : (
        <Grid container spacing={3} sx={{ px: 3 }}>
          {Object.entries(entityConfig.entityStructure.fields).map(
            ([key, field]) => {
              if (key === "id" && !formData[key]) return null;
              return (
                <Grid
                  item
                  xs={field.viewGridXs || 12}
                  sm={field.viewGridSm || 4}
                  key={key}
                >
                  {renderField(key, field, formData[key], [], false)}
                </Grid>
              );
            }
          )}
        </Grid>
      )}
      {bulkUpdateField && (
        <BulkUpdateDialog
          title={`Bulk Update ${bulkUpdateField.key}`}
          open={openDialog}
          onClose={() => {
            setBulkUpdateField(null);
            setOpenDialog(false);
          }}
          onBulkUpdate={async (data) =>
            await handleBulkUpdate(
              bulkUpdateField.key,
              bulkUpdateField.path,
              data,
              bulkUpdateField.index
            )
          }
          validateInput={(data) => {
            const errors = validateField(
              data,
              {
                type: FIELD_TYPES.NESTED_ARRAY,
                label: bulkUpdateField.key,
                nestedFields: bulkUpdateField.nestedFields,
              },
              [], mode
            );
            if (errors.length > 0) {
              throw new Error(errors.join("; "));
            }
          }}
          successMessage={`${bulkUpdateField.key} updated successfully!`}
          helperText={`Enter an array of ${
            bulkUpdateField.key
          } in JSON format (e.g., ${JSON.stringify([
            initializeNestedObject(bulkUpdateField.nestedFields),
          ])})`}
        />
      )}
    </Div>
  );
};

export default GenericEntityForm;